import React, { useEffect, useRef, useState } from 'react';
import { trapTabKey } from 'components/common/calendar/calendarHelpers';

const YEARS_PER_PAGE = 25;

interface YearGridPanelProps {
    selectedYear: number;
    onSelect: (year: number) => void;
    style?: React.CSSProperties;
}

export const YearGridPanel = React.forwardRef<HTMLDivElement, YearGridPanelProps>(
    ({ selectedYear, onSelect, style }, ref) => {
        const [pageStart] = useState(() => Math.floor(selectedYear / YEARS_PER_PAGE) * YEARS_PER_PAGE);
        const years    = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);
        const thisYear = new Date().getFullYear();

        // The grid is portaled out of the popup, so opening it has to hand focus over
        // explicitly — Tab alone would never reach it.
        const panelRef = useRef<HTMLDivElement>(null);
        const selectedRef = useRef<HTMLButtonElement>(null);
        useEffect(() => selectedRef.current?.focus(), []);

        return (
            <div
                ref={element => {
                    panelRef.current = element;
                    if (typeof ref === 'function') ref(element);
                    else if (ref) ref.current = element;
                }}
                className="calendar-year-panel"
                style={style}
                onKeyDown={event => trapTabKey(event, panelRef.current)}
            >
                <div className="calendar-year-grid">
                    {years.map(y => (
                        <button
                            key={y}
                            ref={y === selectedYear ? selectedRef : undefined}
                            className={[
                                'calendar-year-item',
                                y === selectedYear                   ? 'calendar-year-item--selected' : '',
                                y === thisYear && y !== selectedYear ? 'calendar-year-item--today'    : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => onSelect(y)}
                        >
                            {y}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
);
YearGridPanel.displayName = 'YearGridPanel';

export default YearGridPanel;

